import { DatePipe } from '@angular/common';
import { Component, effect, ElementRef, inject, input, OnDestroy, output, viewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterModule } from '@angular/router';
import { IIconForm } from '@campaign-form/campaign-form.interface';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { HasPermissionPipe } from '@core/pipes/has-permission.pipe';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICreator } from '@pages/creators/creators.interface';
import { CreatorsService } from '@pages/creators/creators.service';
import { BannerCardComponent } from '@shared/components/banner-card/banner-card.component';
import { BaseComponent } from '@shared/components/base.component';
import { BreadcrumbComponent, IBreadcrumb } from '@shared/components/breadcrumb/breadcrumb.component';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { ContentContainerComponent } from '@shared/components/content-container/content-container.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { FileUploaderComponent } from '@shared/components/file-uploader/file-uploader.component';
import { FileUploaderService } from '@shared/components/file-uploader/file-uploader.service';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { ImageUtilitiesService } from '@utilities/image-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { filter, Subscription } from 'rxjs';
import { CreatorInvitationButtonComponent } from '../creator-invitation/creator-invitation-button.component';

@Component({
  imports: [
    RouterModule,
    MatButtonModule,
    MatMenuModule,
    HasPermissionDirective,
    FormsModule,
    BannerCardComponent,
    TranslateModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatErrorComponent,
    FileUploaderComponent,
    DatePipe,
    ContentContainerComponent,
    HasPermissionPipe,
    NgxSkeletonLoaderModule,
    CreatorInvitationButtonComponent,
    IconComponent,
    DotsSpinnerComponent,
  ],
  selector: 'app-creator-details-banner',
  templateUrl: './creator-details-banner.component.html',
})
export class CreatorDetailsBannerComponent extends BaseComponent implements OnDestroy {
  public readonly creator = input<ICreator | null>(null);
  public readonly creatorMode = input<boolean>(false);
  public readonly adminMode = input<boolean>(false);
  public readonly nameForm = input.required<FormGroup<{ name: FormControl<string | null> }>>();
  public readonly iconForm = input.required<FormGroup<IIconForm>>();
  public readonly isEditingName = input<boolean>(false);
  public readonly nameSaving = input<boolean>(false);
  public readonly nameErrorMessage = input<string | null>(null);

  public readonly nameEditingChanged = output<boolean>();
  public readonly imageSelected = output<{ file: File; id: string }>();
  public readonly imageRemoved = output<void>();
  public readonly nameSaveRequested = output<void>();

  public breadcrumbs!: IBreadcrumb[];

  private readonly nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');
  private readonly fileUploaderComponent = viewChild<FileUploaderComponent>('fileUploaderComponent');

  private readonly dialog = inject(MatDialog);
  private readonly creatorsService = inject(CreatorsService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  private readonly subscriptions: Subscription[] = [];

  constructor() {
    super();

    effect(() => {
      this.updateBreadcrumbs();
    });

    this.nameFormSubscription();
  }

  public onChangeNameClick(): void {
    this.nameForm().patchValue({ name: this.creator()?.name ?? '' });
    this.nameEditingChanged.emit(true);

    setTimeout(() => {
      this.nameInput()?.nativeElement.focus();
    });
  }

  public onImageSelected(event: { file: File; id: string }): void {
    const imageFileControl = this.iconForm().get('imageFile');
    const iconControl = this.iconForm().get('icon');

    imageFileControl?.setValue(event.file);
    imageFileControl?.markAsDirty();
    imageFileControl?.updateValueAndValidity();
    iconControl?.setValue(event.id);

    if (imageFileControl?.valid) {
      this.imageSelected.emit(event);
    }
  }

  public onImageRemoved(): void {
    const imageFileControl = this.iconForm().get('imageFile');
    const iconControl = this.iconForm().get('icon');

    imageFileControl?.setValue(null);
    imageFileControl?.markAsDirty();
    imageFileControl?.updateValueAndValidity();
    iconControl?.setValue(null);

    this.imageRemoved.emit();
  }

  public onChangeImageClick(): void {
    const fileUploader = this.fileUploaderComponent();

    if (fileUploader) {
      fileUploader.onUploadNewClick();
    }
  }

  public onDeleteCreatorClick(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('shared.cancel'),
        confirmText: this.translate.instant('shared.delete'),
        message: this.translate.instant('pages.creators.datatable.deleteCreatorMessage', {
          name: this.creator()?.name,
        }),
        title: this.translate.instant('pages.creators.datatable.deleteCreator'),
      },
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.creatorsService.deleteCreator(this.creator()?.id!).subscribe(() => {
          this.router.navigate(['/creators']);
        });
      });
  }

  public onSaveClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.creator() || !this.nameForm().valid || this.nameSaving()) {
      return;
    }

    this.nameSaveRequested.emit();
  }

  public onDiscardClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.nameSaving()) {
      return;
    }

    this.nameForm().patchValue({ name: this.creator()?.name ?? '' });
    this.nameEditingChanged.emit(false);
  }

  public setCreator(creator: ICreator | null, emitEvent = true): void {
    if (creator) {
      this.iconForm().patchValue(
        {
          icon: creator.icon,
          imageFile: ImageUtilitiesService.isValidFileId(creator.icon)
            ? FileUploaderService.createCustomFile(creator.icon!)
            : null,
        },
        { emitEvent },
      );
    }

    this.updateBreadcrumbs();
  }

  public updateBreadcrumbs(): void {
    this.breadcrumbs = this.creatorMode()
      ? []
      : [
          { label: this.translate.instant('pages.creators.banner.title'), link: '/creators' },
          { label: this.creator()?.name ?? BreadcrumbComponent.loadingLabel },
        ];
  }

  public ngOnDestroy(): void {
    this.subscriptions?.forEach((subscription) => subscription?.unsubscribe());
  }

  private nameFormSubscription(): void {
    effect(() => {
      const subscription = this.nameForm()
        .get('name')
        ?.valueChanges.subscribe((name) => {
          if (this.isEditingName() && (!name || name.trim() === '')) {
            this.nameForm().patchValue({ name: '' }, { emitEvent: false });

            return;
          }
        });

      if (subscription) {
        this.subscriptions.push(subscription);
      }
    });
  }
}
